import * as Sentry from "@sentry/react"
import { QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { createRouter, RouterProvider } from "@tanstack/react-router"

import PendingComponent from "./globalComponents/atoms/PendingComponent"
import { routeTree } from "./routeTree.gen"
import { ENV } from "./utilities/config"
import { useTheme } from "./utilities/hooks/useTheme"

const queryClient = new QueryClient()

export const router = createRouter({
  routeTree,
  defaultPreload: false,
  defaultPreloadStaleTime: 0,
  context: {
    queryClient,
    setScannedArea: undefined!,
    setDocumentThemeMode: undefined!,
    /*
      Bang because we are passing it before the router
      enters react land and we don't want to check
      optionality every time we use it :/
    */
  },
  defaultPendingComponent: PendingComponent,
  notFoundMode: "root",
  unmaskOnReload: true,
})

// Register router for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

const isProd = ENV === "production"

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: ENV,
  release: APP_VERSION,
  integrations: [
    Sentry.tanstackRouterBrowserTracingIntegration(router),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: isProd ? 0.1 : 1.0, // 10% in production, 100% otherwise
  replaysSessionSampleRate: isProd ? 0.1 : 1.0, // 10% in production, 100% otherwise
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

function App() {
  const { setScannedArea, setDocumentThemeMode } = useTheme()
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider
        router={router}
        context={{
          setScannedArea,
          setDocumentThemeMode,
        }}
      />
    </QueryClientProvider>
  )
}

export default App
